/**
 * Date utilities for consistent timezone handling.
 *
 * Backend stores competition dates as full ISO 8601 UTC timestamps.
 * Frontend sends full ISO datetime (browser local time converted to UTC)
 * and receives full ISO datetime from backend.
 * Frontend displays dates in the user's local timezone.
 */

/**
 * Convert a JavaScript Date to a YYYY-MM-DD string.
 * Uses the browser's local timezone to determine the calendar date.
 * @param {Date} date - JavaScript Date object
 * @returns {string} YYYY-MM-DD
 */
export function dateToISODate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Parse a date string to a JavaScript Date.
 * - Full ISO datetime (e.g. '2026-05-31T22:00:00.000Z'): parsed as UTC, converted to local
 * - Plain date (e.g. '2026-06-01'): parsed as local midnight
 * @param {string} dateStr - Date string
 * @returns {Date|null} JavaScript Date at local time
 */
export function parseLocalDate(dateStr) {
    if (!dateStr) return null;
    // If it contains a time component, parse as full datetime (handles UTC correctly)
    if (dateStr.includes('T')) {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return null;
        return d;
    }
    // Plain YYYY-MM-DD: parse as local midnight
    const datePart = dateStr.substring(0, 10);
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day);
}

/**
 * Format a date string for display using date-fns.
 * Accepts YYYY-MM-DD or ISO format strings.
 * @param {string} dateStr - Date string
 * @param {string} formatStr - date-fns format string
 * @returns {string} Formatted date
 */
export function formatDate(dateStr, formatStr) {
    if (!dateStr) return '';
    const date = parseLocalDate(dateStr);
    if (!date || isNaN(date.getTime())) return '';
    const { format } = require('date-fns');
    return format(date, formatStr);
}
